/**
 * Centralized environment configuration for the landing page.
 *
 * Next.js requires NEXT_PUBLIC_ prefix for client-side env vars.
 * Server-only vars (no prefix) are only accessible in server components/routes.
 *
 * For the monorepo root .env to work with Next.js landing-page:
 * - NEXT_PUBLIC_APP_URL must be set in .env (for client components)
 * - NEXT_PUBLIC_BASE_URL must be set in .env (for API calls)
 * - NEXT_PUBLIC_SUPABASE_URL must be set in .env (for Supabase client)
 * - NEXT_PUBLIC_SUPABASE_ANON_KEY must be set in .env (for Supabase client)
 */

// `??` preserves an intentionally-empty NEXT_PUBLIC_APP_URL so client links
// like `${APP_URL}/login` resolve to same-origin `/login` — which Next's dev
// rewrite proxies to FRONTEND_PROXY_URL and DO's prod routing sends to the
// React app. Set an absolute URL only when you need cross-origin links.
const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? '';
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:8440';
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? 'http://localhost:3000';
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || 'http://localhost:54321';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
// Same Sentry project (dd-ai-agent) as the React app — landing events carry a
// `service: landing-page` tag. Empty = Sentry disabled (local dev default).
const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN || '';

// Empty defaults are deliberate. `/api/plans` and `resolveCouponForPlanType`
// treat empty as "no Stripe code configured for this billing period" and
// short-circuit to no discount — that's the "Option B — Hard disable"
// lifecycle op documented in docs/features/COUPON_PRICING.md. Hard-coding a
// fallback here would break that disable path.
const COUPON_MONTHLY = process.env.NEXT_PUBLIC_COUPON_MONTHLY ?? '';
const COUPON_YEARLY = process.env.NEXT_PUBLIC_COUPON_YEARLY ?? '';
const STRIPE_SECRET_KEY = process.env.STRIPE_SECRET_KEY || '';

export const config = {
  APP_URL,
  BASE_URL,
  SITE_URL,
  SUPABASE_URL,
  SUPABASE_ANON_KEY,
  FF_UPGRADE_FLOW: process.env.NEXT_PUBLIC_FF_UPGRADE_FLOW,
  FF_NEWSLETTER_SIGNUP: process.env.NEXT_PUBLIC_FF_NEWSLETTER_SIGNUP,
  POSTHOG_KEY,
  POSTHOG_HOST,
  SENTRY_DSN,
  COUPON_MONTHLY,
  COUPON_YEARLY,
  STRIPE_SECRET_KEY,
};

export { APP_URL, BASE_URL, SITE_URL, SUPABASE_URL, SUPABASE_ANON_KEY, POSTHOG_KEY, POSTHOG_HOST, SENTRY_DSN, COUPON_MONTHLY, COUPON_YEARLY, STRIPE_SECRET_KEY };
